import { useEffect, useState, type Dispatch, type SetStateAction } from 'react';
import { Button, Col, Row, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ChildrensTooltip from '@/components/common/ChildrensTooltip';
import AddKeywordsDrawer from './drawers/AddKeywordsDrawer';
import UpdateSettingsDrawer from '@/app/KeywordTracker/components/keywords/drawers/UpdateSettingsDrawer';
import ManualUpdateSettingsDrawer from '@/app/KeywordTracker/components/keywords/drawers/ManualUpdateSettingsDrawer';
import useDomain from '@/hooks/useDomain';
import { useGetTariffInfoQuery } from '../../api/keywordsApi';
import {
  isTrackerKeywordsTariffZero,
  TRACKER_KEYWORDS_INACTIVE_TOOLTIP
} from '../../utils/trackerTariff';
import type { GridRef } from './drawers/types';

interface AddUpdateButtonsProps {
  isOnUpdate: boolean;
  setIsOnUpdate: Dispatch<SetStateAction<boolean>>;
  gridRef: GridRef;
  setShouldRevalidateFirstPage: (value: boolean) => void;
}

const AddUpdateButtons = ({
  isOnUpdate,
  setIsOnUpdate,
  gridRef,
  setShouldRevalidateFirstPage
}: AddUpdateButtonsProps) => {
  const { domain, domain_slug } = useDomain();
  const { data: trackerInfo, isFetching: isFetchingTrackerInfo } =
    useGetTariffInfoQuery(domain_slug);
  const [showAddKeywordsDrawer, setShowAddKeywordsDrawer] = useState(false);
  const [showUpdateSettingsDrawer, setShowUpdateSettingsDrawer] =
    useState(false);
  const [showManualUpdateSettingsDrawer, setShowManualUpdateSettingsDrawer] =
    useState(false);

  useEffect(() => {
    if (trackerInfo?.data) {
      setIsOnUpdate(trackerInfo.data.updating === true);
    }
  }, [trackerInfo]);

  const isKeywordsOver = () =>
    trackerInfo?.data?.used?.num_of_keywords >
    trackerInfo?.data?.tariff?.num_of_keywords;

  const isDomainsOver = () =>
    Number(
      (trackerInfo?.data?.used as Record<string, unknown> | undefined)
        ?.num_of_domains ?? 0
    ) >
    Number(
      (trackerInfo?.data?.tariff as Record<string, unknown> | undefined)
        ?.num_of_domains ?? 0
    );

  const isCompetitorsOver = () =>
    trackerInfo?.data?.used?.num_of_competitors >
    trackerInfo?.data?.tariff?.num_of_competitors;

  const isTrackerAddonInactive = () =>
    isTrackerKeywordsTariffZero(trackerInfo);

  const isUpdateDisabled = () =>
    isTrackerAddonInactive() ||
    isKeywordsOver() ||
    isDomainsOver() ||
    isCompetitorsOver();


  const isManualUpdateDisabled = () =>
    trackerInfo?.data?.enable_automatic_update;

  const isAccessAllowed = () => trackerInfo?.data?.access_level !== 3;

  const getActionDisabledTooltip = (): string => {
    if (isTrackerAddonInactive()) {
      return TRACKER_KEYWORDS_INACTIVE_TOOLTIP;
    }
    if (isUpdateDisabled()) {
      return 'تعداد دامنه ها و رقبا یا کلمات کلیدی با طرح خریداری شده مطابقت ندارد.';
    }
    if (!isAccessAllowed()) {
      return 'شما دسترسی لازم را ندارید';
    }
    return '';
  };

  const getManualUpdateTooltip = (): string => {
    if (isManualUpdateDisabled()) {
      return 'به‌روزرسانی خودکار فعال است';
    }
    if (isOnUpdate) {
      return 'به‌روزرسانی در حال انجام است';
    }
    return getActionDisabledTooltip();
  };

  return (
    <Col sm="auto">
      <Row className="g-2 mb-3 mb-md-0">
        <Col xs={12} md="auto">
          <ChildrensTooltip
            placement="top"
            className="w-100"
            html={getActionDisabledTooltip()}
          >
            <Button
              disabled={
                isFetchingTrackerInfo ||
                isUpdateDisabled() ||
                !isAccessAllowed()
              }
              onClick={() => setShowAddKeywordsDrawer(true)}
              variant="falcon-primary"
              size="sm"
              className="d-flex gap-2 w-100 align-items-center justify-content-center text-nowrap"
            >
              {isTrackerAddonInactive() ? (
                <FontAwesomeIcon icon="lock" className="fs--1" />
              ) : (
                <FontAwesomeIcon icon="plus" />
              )}
              افزودن کلمات
              {isUpdateDisabled() && !isTrackerAddonInactive() && (
                <FontAwesomeIcon icon="lock" className="fs--1" />
              )}
            </Button>
          </ChildrensTooltip>
        </Col>
        <Col xs={6} md="auto">
          <ChildrensTooltip
            placement="top"
            html={getActionDisabledTooltip()}
          >
            <Button
              disabled={
                isFetchingTrackerInfo ||
                isUpdateDisabled() ||
                !isAccessAllowed()
              }
              onClick={() => setShowUpdateSettingsDrawer(true)}
              variant="secondary"
              size="sm"
              className="d-flex gap-2 w-100 align-items-center justify-content-center text-nowrap"
            >
              {isTrackerAddonInactive() ? (
                <FontAwesomeIcon icon="lock" className="fs--1" />
              ) : (
                <FontAwesomeIcon icon="sync-alt" />
              )}
              به‌روزرسانی خودکار
              {isUpdateDisabled() && !isTrackerAddonInactive() && (
                <FontAwesomeIcon icon="lock" className="fs--1" />
              )}
            </Button>
          </ChildrensTooltip>
        </Col>
        <Col xs={6} md="auto">
          <ChildrensTooltip
            placement="top"
            html={getManualUpdateTooltip()}
          >
            <Button
              disabled={
                isFetchingTrackerInfo ||
                isUpdateDisabled() ||
                isManualUpdateDisabled() ||
                isOnUpdate ||
                !isAccessAllowed()
              }
              onClick={() => setShowManualUpdateSettingsDrawer(true)}
              variant="secondary"
              size="sm"
              className="d-flex gap-2 w-100 align-items-center justify-content-center text-nowrap"
            >
              {isOnUpdate ? (
                <Spinner
                  animation="border"
                  style={{
                    width: '18px',
                    marginBottom: '-3px',
                    paddingLeft: '3px',
                    height: '18px'
                  }}
                />
              ) : isTrackerAddonInactive() ? (
                <FontAwesomeIcon icon="lock" className="fs--1" />
              ) : (
                <FontAwesomeIcon icon="hand" />
              )}
              {isOnUpdate ? 'درحال به‌روزرسانی' : 'به‌روزرسانی دستی'}
              {isUpdateDisabled() && !isTrackerAddonInactive() && (
                <FontAwesomeIcon icon="lock" className="fs--1" />
              )}
            </Button>
          </ChildrensTooltip>
        </Col>
      </Row>

      {domain && domain_slug && (
        <AddKeywordsDrawer
          gridRef={gridRef}
          setShouldRevalidateFirstPage={setShouldRevalidateFirstPage}
          show={showAddKeywordsDrawer}
          setShow={setShowAddKeywordsDrawer}
        />
      )}
      <UpdateSettingsDrawer
        setShow={setShowUpdateSettingsDrawer}
        show={showUpdateSettingsDrawer}
      />
      <ManualUpdateSettingsDrawer
        isOnUpdate={isOnUpdate}
        setIsOnUpdate={setIsOnUpdate}
        setShow={setShowManualUpdateSettingsDrawer}
        show={showManualUpdateSettingsDrawer}
      />
    </Col>
  );
};

export default AddUpdateButtons;
